import { useQueries, useQuery } from '@tanstack/react-query';
import {
  availabilityCataloguePath,
  employeesPath,
  itemCategoriesPath,
  itemUnitsPath,
  vendorsPath,
} from '@core/api/queryPaths';

import { apiFetch } from './client';

// The types live in the shared core (docs/Mobile_Apps_Design.md); re-exported so imports stay put.
export type * from '@core/api/masters';
import type {
  AvailabilitySearchFilters,
  AvailabilitySearchResult,
  CatalogueAvailability,
  Branch,
  BranchCalendar,
  CategoryAttribute,
  Customer,
  CustomerFilters,
  Employee,
  EmployeeFilters,
  FinancialYear,
  Item,
  ItemAttribute,
  ItemCategory,
  ItemFilters,
  ItemMedia,
  ItemUnit,
  ItemUnitFilters,
  ItemVariant,
  ItemVariantAxisOptions,
  Paged,
  Party,
  PartyDuplicate,
  PartyFilters,
  RateSlabRow,
  Role,
  UnitCalendar,
  User,
  Vendor,
  VendorFilters,
} from '@core/api/masters';

export const itemCategoriesKey = ['item-category'] as const;

/**
 * Branch-scoped (spec rule 14), so the acting branch has to be part of the cache key — otherwise
 * switching branches keeps serving the previous branch's rows until something else happens to
 * invalidate `itemCategoriesKey`, which silently shows the wrong shop's categories.
 */
export function useItemCategories(branchId: number | undefined, includeInactive = false) {
  return useQuery({
    queryKey: [...itemCategoriesKey, { branchId, includeInactive }],
    queryFn: () => apiFetch<ItemCategory[]>(itemCategoriesPath(includeInactive)),
    enabled: branchId !== undefined,
  });
}

export const itemsKey = ['item'] as const;

/**
 * Branch-scoped like the categories are, so the acting branch is part of the cache key — without
 * it, switching branches keeps serving the previous shop's catalogue.
 *
 * The filters are in the key too, which is what makes paging and search feel instant on the way
 * back: a page already fetched is still in the cache under its own key rather than being refetched.
 */
export function useItems(branchId: number | undefined, filters: ItemFilters = {}) {
  const { search, categoryId, includeInactive = false, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...itemsKey, { branchId, search, categoryId, includeInactive, page, pageSize }],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });

      if (search) {
        params.set('search', search);
      }

      if (categoryId !== undefined) {
        params.set('categoryId', String(categoryId));
      }

      if (includeInactive) {
        params.set('includeInactive', 'true');
      }

      return apiFetch<Paged<Item>>(`/api/item?${params.toString()}`);
    },
    enabled: branchId !== undefined,
    // A page that is being replaced by the next one should not blank the table out — keeping the
    // last result visible while the new one loads is what stops the whole screen flickering on
    // every keystroke of a search.
    placeholderData: (previous) => previous,
  });
}

export const itemAttributesKey = ['item-attribute'] as const;

/**
 * Branch-scoped like the categories and items are (spec rule 14), so the acting branch belongs in
 * the cache key — without it, switching branches keeps serving the previous shop's attributes.
 */
export function useItemAttributes(branchId: number | undefined, includeInactive = false) {
  return useQuery({
    queryKey: [...itemAttributesKey, { branchId, includeInactive }],
    queryFn: () =>
      apiFetch<ItemAttribute[]>(
        `/api/item-attribute${includeInactive ? '?includeInactive=true' : ''}`,
      ),
    enabled: branchId !== undefined,
  });
}

export const categoryAttributesKey = ['item-category', 'attributes'] as const;

/**
 * The attributes that apply to one category. Keyed by category rather than fetched with the tree,
 * because inheritance is resolved server-side per category and the list screen never needs it.
 */
export function useCategoryAttributes(categoryId: number | undefined) {
  return useQuery({
    queryKey: [...categoryAttributesKey, { categoryId }],
    queryFn: () => apiFetch<CategoryAttribute[]>(`/api/item-category/${categoryId}/attributes`),
    enabled: categoryId !== undefined,
  });
}

export const itemVariantsKey = ['item', 'variants'] as const;

/**
 * Every configuration of one design. Keyed by item rather than fetched with the catalogue, because
 * a chain carries thousands of designs and a variants list is only ever looked at one at a time.
 */
export function useItemVariants(itemId: number | undefined) {
  return useQuery({
    queryKey: [...itemVariantsKey, { itemId }],
    queryFn: () => apiFetch<ItemVariant[]>(`/api/item/${itemId}/variants`),
    enabled: itemId !== undefined,
  });
}

export const itemVariantAxesKey = ['item', 'variant-axes'] as const;

/**
 * The axes this design's garments are told apart by. An empty array is a real answer — the item's
 * category declares no variant-forming attribute yet — and the drawer says so rather than showing
 * an empty form.
 */
export function useItemVariantAxes(itemId: number | undefined) {
  return useQuery({
    queryKey: [...itemVariantAxesKey, { itemId }],
    queryFn: () => apiFetch<ItemVariantAxisOptions[]>(`/api/item/${itemId}/variants/axes`),
    enabled: itemId !== undefined,
  });
}

/**
 * The same answer for several designs at once, keyed by item id.
 *
 * <p>
 * Exists for the stock-intake screen, where the lines of one delivery name different designs and
 * each design declares its own axes. The alternative — a component per line calling
 * `useItemVariantAxes` — makes the page unable to validate itself: whether a line is complete
 * depends on axes only the child knows about, so the parent ends up mirroring them back up through
 * callbacks and the two copies drift.
 * </p>
 *
 * <p>
 * Every query uses `useItemVariantAxes`'s own key, so the cache is shared with it rather than
 * duplicated: a design already fetched by the single-item drawer is not fetched again here.
 * </p>
 */
export function useItemVariantAxesFor(itemIds: readonly number[]) {
  const distinct = [...new Set(itemIds)];

  const results = useQueries({
    queries: distinct.map((itemId) => ({
      queryKey: [...itemVariantAxesKey, { itemId }],
      queryFn: () => apiFetch<ItemVariantAxisOptions[]>(`/api/item/${itemId}/variants/axes`),
    })),
  });

  const axes: Record<number, ItemVariantAxisOptions[]> = {};

  distinct.forEach((itemId, index) => {
    const data = results[index]?.data;

    if (data) {
      axes[itemId] = data;
    }
  });

  return {
    axes,
    // An unresolved design must not read as "no axes to choose", which would let an incomplete
    // line look complete. The screen waits rather than validating against a half-loaded map.
    isLoading: results.some((result) => result.isLoading),
  };
}

export const itemUnitsKey = ['item-unit'] as const;

/**
 * The rail at the acting branch (spec Section 6).
 *
 * Branch-scoped like everything else here, and more strictly: a garment hangs in exactly one shop,
 * so the branch is not merely part of the cache key but part of what the row *means*.
 */
export function useItemUnits(branchId: number | undefined, filters: ItemUnitFilters = {}) {
  const { itemId, variantId, status, search, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...itemUnitsKey, { branchId, itemId, variantId, status, search, page, pageSize }],
    queryFn: () => apiFetch<Paged<ItemUnit>>(itemUnitsPath(filters)),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export const availabilityKey = ['availability'] as const;

/**
 * "Black tuxedo, size 40, 12–14 March" — which garments could take this booking.
 *
 * Branch-scoped like everything below the catalogue, and more strictly: a garment hangs in exactly
 * one shop, so the branch is part of what the answer *means* and not merely part of the cache key.
 */
export function useAvailabilitySearch(
  branchId: number | undefined,
  filters: AvailabilitySearchFilters,
) {
  const { itemId, fromDate, toDate, axisValueIds } = filters;

  return useQuery({
    queryKey: [...availabilityKey, 'search', { branchId, itemId, fromDate, toDate, axisValueIds }],
    queryFn: () => {
      const params = new URLSearchParams({
        itemId: String(itemId),
        fromDate: String(fromDate),
        toDate: String(toDate),
      });

      for (const valueId of axisValueIds ?? []) {
        params.append('axisValueIds', String(valueId));
      }

      return apiFetch<AvailabilitySearchResult>(`/api/availability/search?${params.toString()}`);
    },
    enabled: branchId !== undefined && itemId !== undefined && !!fromDate && !!toDate,
    // Availability is the one read where stale is actively harmful: someone else may have taken the
    // garment while this screen sat open, so it refetches on focus rather than trusting the cache.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

/**
 * A page of the catalogue over a booking's dates — every stocked design with all its garments, in
 * one call. Never served stale, for the reason the single search is not.
 */
export function useCatalogue(
  branchId: number | undefined,
  filters: {
    fromDate: string | undefined;
    toDate: string | undefined;
    search?: string | undefined;
    categoryId?: number | undefined;
    page?: number | undefined;
    pageSize?: number | undefined;
  },
) {
  const { fromDate, toDate } = filters;

  return useQuery({
    queryKey: [...availabilityKey, 'catalogue', { branchId, ...filters }],
    queryFn: () =>
      apiFetch<CatalogueAvailability>(
        availabilityCataloguePath({ ...filters, fromDate: fromDate!, toDate: toDate! }),
      ),
    enabled: branchId !== undefined && !!fromDate && !!toDate,
    staleTime: 0,
    refetchOnWindowFocus: true,
    placeholderData: (previous) => previous,
  });
}

export function useUnitCalendar(
  unitId: number | undefined,
  fromDate: string,
  toDate: string,
  enabled = true,
) {
  return useQuery({
    queryKey: [...availabilityKey, 'calendar', { unitId, fromDate, toDate }],
    queryFn: () =>
      apiFetch<UnitCalendar>(`/api/availability/unit/${unitId}?from=${fromDate}&to=${toDate}`),
    enabled: enabled && unitId !== undefined,
  });
}

/**
 * The whole rail over a month — what the search beside it deliberately will not answer.
 *
 * Branch-scoped in the key for the reason the search is: a garment hangs in exactly one shop, so
 * the acting branch is part of what the answer *means*, and a cache shared across a switch would
 * show the Rajkot rail under the Morbi heading.
 *
 * `staleTime: 0` and a refetch on focus, again matching the search — somebody else may have taken a
 * garment while this page sat open on a counter, and a calendar is the one screen where a stale
 * answer gets a garment promised twice.
 */
export function useBranchCalendar(
  branchId: number | undefined,
  fromDate: string,
  toDate: string,
  options: { itemId?: number | undefined; unitId?: number | undefined; onlyBusy?: boolean } = {},
) {
  const { itemId, unitId, onlyBusy = false } = options;

  return useQuery({
    queryKey: [
      ...availabilityKey,
      'branch',
      { branchId, fromDate, toDate, itemId, unitId, onlyBusy },
    ],
    queryFn: () => {
      const params = new URLSearchParams({ fromDate, toDate });

      // A scanned garment wins over a picked design, the same way the server resolves it — sending
      // both and letting them intersect answers an empty page whenever they disagree.
      if (unitId !== undefined) {
        params.set('unitId', String(unitId));
      } else if (itemId !== undefined) {
        params.set('itemId', String(itemId));
      }

      if (onlyBusy) {
        params.set('onlyBusy', 'true');
      }

      return apiFetch<BranchCalendar>(`/api/availability/calendar?${params.toString()}`);
    },
    enabled: branchId !== undefined,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export const customersKey = ['customer'] as const;

/**
 * Paged like the item catalogue, and for the same reason: a shop with four branches and a few
 * wedding seasons behind it carries thousands of customers, not tens.
 */
export function useCustomers(branchId: number | undefined, filters: CustomerFilters = {}) {
  const { search, includeInactive = false, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...customersKey, { branchId, search, includeInactive, page, pageSize }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

      if (search) {
        params.set('search', search);
      }

      if (includeInactive) {
        params.set('includeInactive', 'true');
      }

      return apiFetch<Paged<Customer>>(`/api/customer?${params.toString()}`);
    },
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export const vendorsKey = ['vendor'] as const;

/**
 * Paged like the customer master rather than fetched whole. A vendor list is far shorter — tens,
 * not thousands — but one shape for both party masters is worth more than the rows saved, and a
 * chain keeping branch-local launderers still outgrows a dropdown.
 */
export function useVendors(branchId: number | undefined, filters: VendorFilters = {}) {
  const { search, capability, includeInactive = false, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...vendorsKey, { branchId, search, capability, includeInactive, page, pageSize }],
    queryFn: () =>
      apiFetch<Paged<Vendor>>(vendorsPath({ search, capability, includeInactive, page, pageSize })),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export const employeesKey = ['employee'] as const;

/**
 * Paged, in the shape both party masters use.
 *
 * <p>
 * <strong>Keyed on the branch, and not by copying `useVendors`.</strong> An employee is a shared
 * master: a head-office row is visible everywhere and a branch-local one only where that person is
 * posted, so what comes back depends on the acting branch and the cache has to say so or switching
 * branch shows the previous one's staff.
 * </p>
 */
export function useEmployees(branchId: number | undefined, filters: EmployeeFilters = {}) {
  const { search, capability, includeInactive = false, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...employeesKey, { branchId, search, capability, includeInactive, page, pageSize }],
    queryFn: () =>
      apiFetch<Paged<Employee>>(
        employeesPath({ search, capability, includeInactive, page, pageSize }),
      ),
    enabled: branchId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export const partiesKey = ['party'] as const;

/**
 * Not keyed on the branch, unlike every other master hook here — and that is the point rather than
 * an omission. A party carries no branch at all, and its roles are counted past the branch filter,
 * because the question is whether the *company* knows this person. Keying the cache on the acting
 * branch would split one answer into one per counter.
 */
export function useParties(filters: PartyFilters = {}) {
  const { search, role, page = 1, pageSize = 25 } = filters;

  return useQuery({
    queryKey: [...partiesKey, { search, role, page, pageSize }],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });

      if (search) {
        params.set('search', search);
      }

      if (role) {
        params.set('role', role);
      }

      return apiFetch<Paged<Party>>(`/api/party?${params.toString()}`);
    },
    placeholderData: (previous) => previous,
  });
}

/**
 * The numbers held by more than one identity.
 *
 * An empty answer is the thing a shop is working towards: it is what says every person is one
 * record, and what the unique index on the number already depends on.
 */
export function usePartyDuplicates() {
  return useQuery({
    queryKey: [...partiesKey, 'duplicates'],
    queryFn: () => apiFetch<PartyDuplicate[]>('/api/party/duplicates'),
  });
}

export const rateSlabsKey = ['rate-slab'] as const;

/**
 * Every band visible at the acting branch — the company's shared cards and this branch's own.
 *
 * Branch-scoped like the rest of the catalogue, so the acting branch belongs in the cache key.
 *
 * The key is deliberately the bare `rate-slab` prefix that the booking screen's per-design resolve
 * queries already sit under, so publishing a band invalidates the counter's quotes as well as this
 * list. A shop that has just raised its sherwani rate must not keep quoting the old one to the
 * customer standing in front of it.
 */
export function useRateSlabs(branchId: number | undefined, includeInactive = false) {
  return useQuery({
    queryKey: [...rateSlabsKey, { branchId, includeInactive }],
    queryFn: () =>
      apiFetch<RateSlabRow[]>(`/api/rate-slab${includeInactive ? '?includeInactive=true' : ''}`),
    enabled: branchId !== undefined,
  });
}

export const branchesKey = ['branch'] as const;
export const rolesKey = ['role'] as const;
export const financialYearsKey = ['financial-year'] as const;

export function useBranches(includeInactive = false) {
  return useQuery({
    queryKey: [...branchesKey, { includeInactive }],
    queryFn: () =>
      apiFetch<Branch[]>(`/api/branch${includeInactive ? '?includeInactive=true' : ''}`),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: rolesKey,
    queryFn: () => apiFetch<Role[]>('/api/role'),
  });
}

export const usersKey = ['user'] as const;

/**
 * Every account, for a screen that has to choose among them.
 *
 * Unpaged, matching the endpoint: seats are a plan limit measured in tens, so a shop's whole user
 * list is a short list by construction — the reason the Users screen has no pagination either.
 */
export function useUsers(includeInactive = false) {
  return useQuery({
    queryKey: [...usersKey, { includeInactive }],
    queryFn: () => apiFetch<User[]>(`/api/user${includeInactive ? '?includeInactive=true' : ''}`),
  });
}

export function useFinancialYears() {
  return useQuery({
    queryKey: financialYearsKey,
    queryFn: () => apiFetch<FinancialYear[]>('/api/financial-year'),
  });
}

export const itemMediaKey = ['item-media'] as const;

export function useItemMedia(itemId: number | undefined) {
  return useQuery({
    queryKey: [...itemMediaKey, itemId],
    queryFn: () => apiFetch<ItemMedia[]>(`/api/item/${itemId}/media`),
    enabled: itemId !== undefined,
    // The URLs inside expire, so a long-cached gallery renders broken images. Refetching on focus
    // is cheaper than tracking each link's expiry.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export const unitMediaKey = ['item-unit-media'] as const;

/**
 * One physical garment's own gallery.
 *
 * <p>
 * Separate from the design's, and separately keyed, because they are different pictures answering
 * different questions: the design's is the studio shot that sells a sherwani, and this is what
 * <em>this</em> copy currently looks like. Same expiry problem, same short cache.
 * </p>
 */
export function useUnitMedia(unitId: number | undefined) {
  return useQuery({
    queryKey: [...unitMediaKey, unitId],
    queryFn: () => apiFetch<ItemMedia[]>(`/api/item-unit/${unitId}/media`),
    enabled: unitId !== undefined,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
